import React, { useEffect, useState, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { isEqual, debounce } from 'lodash'
import cn from 'classnames'
import InputAdornment from '@material-ui/core/InputAdornment'
import Input from '@material-ui/core/Input'
import ExpandMore from '@material-ui/icons/ExpandMore'
import Search from '@material-ui/icons/Search'
import {
  Button,
  Typography,
  Grid,
  Collapse,
  IconButton,
  Badge,
  Popover,
  Switch,
} from '@material-ui/core'
import { ReactComponent as IconFilter } from 'assets/img/open-filters.svg'
import { ReactComponent as IconFilterClear } from 'assets/img/clear-filters.svg'
import { Add } from '@material-ui/icons'
import { ReactComponent as Close } from 'assets/img/ic_close.svg'
import { saveMyJobsFilters, saveJobsSorting } from 'reducers/profile/reducer'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import { formatMessage } from 'lib/intl'
import {
  resetTasks,
  fetchJobFiltersData,
  fetchJobList,
  setActiveSite,
  setJobFilters,
} from 'reducers/myJobs/reducer'
import Psfs from 'lib/psfs/Psfs'
import SidePopover from 'components/common/Popover/SidePopover'
import { fetchSiteList } from 'reducers/logbookForm/reducer'
import { DEFAULT_JOBS_FILTERS } from 'helpers/myJobs'
import withPromise from 'helpers/withPromise'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import { MobileFilterButton } from './MobileFilterButton/MobileFilterButton'
import SortFilter from './SortFilter'
import messages from '../translate'
import UnitFilter from './UnitFilter'
import AssigneesFilter from './AssigneesFilter'
import CreatorsFilter from './CreatorsFilter'
import JobsFilter from './JobsFilter'
import StatusFilter from './StatusFilter'
import SiteFilter from './SiteFilter'
import { FILTER_NAMES, JOB_PRESETS } from '../Common/constants'
import { formatUnitsData, formatExportUnitsData } from '../Common/formatUnits'

// eslint-disable-next-line max-statements
function FilterBar({
  licensee: { name: licenseeName },
  location,
  setActiveSite,
  setJobFilters,
  saveMyJobsFilters,
  saveJobsSorting,
  createWorkflow,
  allowedToCreate,
  siteList,
  jobProfileFilters,
  jobList,
  jobTask,
  jobFiltersData,
  isLoadJobList,
  fetchJobFiltersData,
  fetchJobList,
  fetchSiteList,
  resetTasks,
  setCanLoadJobs,
  setPage,
  page,
  scrollRef,
  searchValue,
  respectFilters,
  setRespectFilters,
  setSearchValue,
  featureFlags,
  myJobsSorting,
}) {
  const usePrevious = value => {
    const ref = useRef()
    useEffect(() => {
      ref.current = value
    })

    return ref.current
  }
  const {
    site: savedSite,
    preset: savedPreset,
    workflows: savedWorkflows,
    creators: savedCreators,
    triggerRules: savedTriggerRules,
    assignees: savedAssignees,
    locusesAndGroups: savedUnits,
  } = jobProfileFilters.toJS()
  const [isShowBadge, setBadgeState] = useState(false)
  const [isOpenMobileFilter, setMobileFilter] = useState(false)
  const correctPreset =
    savedPreset === JOB_PRESETS.FUTURE ? JOB_PRESETS.ACTIVE : savedPreset
  const [currentPreset, setCurrentPreset] = useState(
    correctPreset || JOB_PRESETS.ACTIVE,
  )
  const [currentSorting, setCurrentSorting] = useState(myJobsSorting)
  const [site, setSite] = useState(savedSite)
  const [jobId, setJobId] = useState(null)
  const [jobNumber, setJobNumber] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [isUrlParsed, setIsUrlParsed] = useState(false)
  const searchRef = React.useRef()
  const [selectedWorkflows, setWorkflows] = useState(savedWorkflows)
  const [selectedAssignees, setAssignees] = useState(savedAssignees)
  const [selectedCreators, setCreators] = useState(savedCreators)
  const [selectedTriggerRules, setTriggerRules] = useState(savedTriggerRules)
  const [selectedUnits, setUnits] = useState(savedUnits)
  const [savedUnitIds, setSavedUnitIds] = useState([])
  const prevLicensee = usePrevious(licenseeName)
  const prevUnits = usePrevious(selectedUnits)
  const prevSite = usePrevious(site)
  const prevPage = usePrevious(page)
  const mainDependencies = [
    selectedWorkflows,
    selectedAssignees,
    selectedCreators,
    selectedTriggerRules,
    selectedUnits,
  ].map(item => JSON.stringify(item))

  const classes = {
    createButton: {
      root: 'sf-create-btn',
    },
  }
  const {
    workflows,
    creators,
    triggerRules,
    assignees,
    locusesAndGroups,
  } = jobFiltersData.toJS()
  const jobs = jobList.toArray()
  const [isOpenFilters, setFiltersState] = useState(false)
  const sitePsfs = {
    psfs: new Psfs(),
  }
  sitePsfs.psfs
    .addFilter('active', 1)
    .addFilter('visibleInUi', 1)
    .addFields(['id', 'name'])

  const toggleFilterBar = () => {
    setFiltersState(prevState => !prevState)
  }

  const clearUrlSearchParams = () => {
    setJobId(null)
    setJobNumber(null)
    window.history.replaceState(null, '', window.location.pathname)
  }

  const saveJobFilters = (params, shouldClearUrl = true) => {
    if (shouldClearUrl) {
      clearUrlSearchParams()
    }

    saveMyJobsFilters({
      my_jobs_filters: params,
    })
  }

  const setSitesFilter = selectedSite => {
    setSite(selectedSite)
    if (prevSite && prevSite !== selectedSite) {
      if (site) {
        saveJobFilters({
          site: selectedSite,
          ...DEFAULT_JOBS_FILTERS,
        })
        fetchJobFiltersData({ site: selectedSite })
      }

      setWorkflows([])
      setCreators([])
      setTriggerRules([])
      setAssignees([])
      setUnits([])
      setSavedUnitIds([])
    }
    clearUrlSearchParams()
  }

  const setUnitsFilter = units => {
    setUnits(units.filter(unit => !!unit))
    setSavedUnitIds([])
    clearUrlSearchParams()
  }

  const setCreatedBy = creatorItems => {
    const userItems = []
    const triggerRuleItems = []

    creatorItems.forEach(({ users: usersData, trigger_rules }) => {
      if (userItems) {
        userItems.push(usersData)
      }

      if (trigger_rules) {
        triggerRuleItems.push(trigger_rules)
      }
    })

    setCreators(userItems)
    setTriggerRules(triggerRuleItems)
    saveJobFilters({ creators: userItems, triggerRules: triggerRuleItems })
    clearUrlSearchParams()
  }

  const handleResetFilters = () => {
    saveJobFilters(DEFAULT_JOBS_FILTERS)

    setWorkflows([])
    setCreators([])
    setTriggerRules([])
    setAssignees([])
    setUnits([])
    setSavedUnitIds([])
  }

  const saveActivePreset = preset => {
    clearUrlSearchParams()
    saveJobFilters({ preset })
    setCurrentPreset(preset)
  }

  const saveSorting = sorting => {
    clearUrlSearchParams()
    saveJobsSorting({
      my_jobs_sorting: sorting,
    })
    setCurrentSorting(sorting)
  }

  const getBadgeState = () => {
    const badgeState =
      !isEqual(selectedWorkflows, DEFAULT_JOBS_FILTERS.workflows) ||
      !isEqual(selectedAssignees, DEFAULT_JOBS_FILTERS.assignees) ||
      !isEqual(selectedCreators, DEFAULT_JOBS_FILTERS.creators) ||
      !isEqual(selectedTriggerRules, DEFAULT_JOBS_FILTERS.triggerRules) ||
      !isEqual(selectedUnits, DEFAULT_JOBS_FILTERS.locusesAndGroups)

    setBadgeState(badgeState)
  }

  useEffect(() => {
    fetchSiteList({ ...sitePsfs })

    if (site && site !== prevSite) {
      fetchJobFiltersData({ site })
    }

    if (location.search) {
      const queryParams = new URLSearchParams(location.search)
      const filter_preset = queryParams.get('filter_preset')
      const site_id = queryParams.get('site_id')
      const job_id = +queryParams.get('job_id')
      const search = queryParams.get('search')
      const respect_filters = queryParams.get('respect_filters')
      const unitIds = queryParams.get('unit_id')

      if (unitIds && unitIds.length) {
        setSavedUnitIds(
          unitIds
            .split(',')
            .map(unitId => +String(unitId).trim())
            .filter(id => Number.isFinite(id) && id !== 0),
        )
      }

      if (respect_filters !== null && respect_filters !== respectFilters) {
        setRespectFilters(+respect_filters)
      }

      if (
        filter_preset != null &&
        filter_preset !== '' &&
        currentPreset !== filter_preset
      ) {
        setCurrentPreset(filter_preset)
      }

      if (site_id != null && site_id !== '') {
        const parsedSiteId = +site_id

        if (Number.isFinite(parsedSiteId) && parsedSiteId !== site) {
          setSite(parsedSiteId)
        }
      }

      if (job_id) {
        setJobId(job_id)
      }

      if (search && search.length) {
        setSearchValue(search)
      }

      if (+search) {
        setJobNumber(+search)
      }
    }

    setIsUrlParsed(true)
  }, [])

  useEffect(() => {
    if (prevLicensee && prevLicensee !== licenseeName) {
      fetchSiteList({ ...sitePsfs })
    }
  }, [licenseeName])

  useEffect(() => {
    if (siteList.size) {
      if (!site) {
        const defaultSite = siteList.get(0)

        setSite(defaultSite.id)
        setActiveSite(defaultSite)
        fetchJobFiltersData({ site: defaultSite.id })
        saveJobFilters({ site: defaultSite.id }, false)
      } else {
        const activeSite =
          siteList.find(siteItem => siteItem.id === site) || siteList.get(0)

        if (activeSite) {
          setActiveSite(activeSite)
          setSite(activeSite.id)
          resetTasks()
        }
      }
    }

    if (prevSite && prevSite !== site) {
      if (site) {
        fetchJobFiltersData({ site })
      }

      setWorkflows([])
      setCreators([])
      setTriggerRules([])
      setAssignees([])
      setUnits([])
    }
  }, [siteList, site])

  const fetchJobs = (params, psfs) => {
    withPromise(fetchJobList, {
      params,
      filters: { psfs },
    }).then(response => {
      if (!response.errors && response.data.length) {
        setCanLoadJobs(true)
      }

      if (page === prevPage && page !== 1) {
        setPage(1)
        scrollRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }
    })
  }

  const fetchJobsDebounced = useCallback(debounce(fetchJobs, 1000), [
    page,
    prevPage,
  ])

  useEffect(() => {
    const psfs = new Psfs()
    const filledFilters = [
      { field: 'workflow', data: selectedWorkflows },
      { field: 'created_by', data: selectedCreators },
      { field: 'created_by_trigger_rule', data: selectedTriggerRules },
      { field: 'assignee', data: selectedAssignees },
    ]

    if (!jobId && !jobNumber) {
      filledFilters.forEach(({ field, data }) => {
        const filteredData = data.filter(item => {
          const filterList = jobFiltersData.toJS()[FILTER_NAMES[field]]

          return Array.isArray(filterList) && filterList.length
            ? filterList.some(({ id }) => id === item)
            : item
        })

        if (filteredData.length) {
          psfs.addFilter(field, filteredData, 'in')
        }
      })
    }

    if (prevUnits && !isEqual(prevUnits, selectedUnits)) {
      saveJobFilters({ locusesAndGroups: selectedUnits })
    }

    if (
      ((isUrlParsed && site && currentPreset) || searchValue || jobNumber) &&
      !isLoadJobList
    ) {
      const hasUrlUnitIds = !!savedUnitIds.length
      const savedSelectedUnits = formatExportUnitsData(
        formatUnitsData(
          hasUrlUnitIds ? [] : selectedUnits,
          locusesAndGroups,
          savedUnitIds,
        ),
      )
      // Until `locusesAndGroups` is loaded, export is empty — still send URL `unit_id` as loci.
      let unitFiltersPayload = selectedUnits

      if (hasUrlUnitIds) {
        unitFiltersPayload = savedSelectedUnits.length
          ? savedSelectedUnits
          : savedUnitIds
              .filter(id => Number.isFinite(id) && id !== 0)
              .map(id => ({ locus: id }))
      }

      const params = {
        site,
        preset: currentPreset,
        ...(currentPreset === JOB_PRESETS.ACTIVE &&
        currentSorting &&
        currentSorting.length &&
        featureFlags.JOBS_APP_SORTING
          ? { sort: currentSorting }
          : {}),
        search: searchValue || jobNumber || '',
        ...(!!unitFiltersPayload.length && {
          allowed_locuses_and_groups: JSON.stringify(unitFiltersPayload),
        }),
      }

      if (!respectFilters && searchValue) {
        params.respect_filters = +respectFilters
      }

      if (!searchValue && jobNumber) {
        setSearchValue(jobNumber)
      }

      if (jobId) {
        params.jobs = [jobId]
      }

      setCanLoadJobs(false)
      psfs.addPagination(page === prevPage ? 1 : page, 20)
      setJobFilters({
        site,
        preset: currentPreset,
        workflow: selectedWorkflows,
        created_by: selectedCreators,
        created_by_trigger_rule: selectedTriggerRules,
        assignee: selectedAssignees,
        units: unitFiltersPayload,
        sort: currentSorting,
      })

      fetchJobsDebounced(params, psfs)
    }

    getBadgeState()
  }, [
    ...mainDependencies,
    currentSorting,
    currentPreset,
    site,
    page,
    jobId,
    jobNumber,
    searchValue,
    respectFilters,
    savedUnitIds,
    JSON.stringify(locusesAndGroups),
    isUrlParsed,
  ])

  const showButtonText =
    jobs && jobs.length
      ? `${formatMessage('show')} ${jobs.length} ${formatMessage('jobs')}`
      : formatMessage('no_results')

  return (
    <div className="sf-filter-bar">
      <Grid container spacing={2} alignItems="center">
        <Grid item sm={2} xs={12} className="sf-filter-status">
          <StatusFilter
            preset={currentPreset}
            saveActivePreset={saveActivePreset}
            jobTask={jobTask}
            resetTasks={resetTasks}
            featureFlags={featureFlags}
          />
        </Grid>
        {currentPreset === JOB_PRESETS.ACTIVE &&
          featureFlags.JOBS_APP_SORTING &&
          !ifWindowWidthLessThan(TABLET_BREAKPOINT) && (
            <Grid item sm={2} xs={12}>
              <SortFilter
                currentSorting={currentSorting}
                saveSorting={saveSorting}
              />
            </Grid>
          )}
        <div className="search-block">
          <Input
            className="search-field"
            ref={searchRef}
            placeholder={formatMessage(messages.search)}
            onChange={({ target: { value } }) => {
              clearUrlSearchParams()
              setSearchValue(value)
            }}
            startAdornment={
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            }
            endAdornment={
              !ifWindowWidthLessThan(TABLET_BREAKPOINT) ? (
                <InputAdornment position="start">
                  <ExpandMore
                    fontSize="small"
                    className="search-clear"
                    onClick={() => setAnchorEl(searchRef.current)}>
                    expand_more
                  </ExpandMore>
                </InputAdornment>
              ) : null
            }
            value={searchValue}
            disableUnderline
          />
          <Popover
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}>
            <div className="search-switch">
              <div className="search-switch-label-jobs">
                {formatMessage(messages.respectFilters)}
              </div>
              <Switch
                onClick={() => setRespectFilters(!respectFilters)}
                checked={respectFilters}
                size="small"
              />
            </div>
          </Popover>
        </div>
        {!ifWindowWidthLessThan(TABLET_BREAKPOINT) && (
          <Grid item>
            <Badge variant="dot" invisible={!isShowBadge}>
              <IconButton className="icon-btn" onClick={toggleFilterBar}>
                <IconFilter />
              </IconButton>
            </Badge>
            <IconButton
              className={cn('icon-btn reset-btn', { disabled: !isShowBadge })}
              onClick={handleResetFilters}
              disabled={!isShowBadge}>
              <IconFilterClear />
            </IconButton>
          </Grid>
        )}
        {ifWindowWidthLessThan(TABLET_BREAKPOINT) &&
          featureFlags.APP_HEADER_V2 && (
            <MobileFilterButton
              siteList={siteList}
              site={site}
              setMobileFilter={setMobileFilter}
              createWorkflow={createWorkflow}
            />
          )}
        {allowedToCreate &&
          (!ifWindowWidthLessThan(TABLET_BREAKPOINT) ||
            !featureFlags.APP_HEADER_V2) && (
            <Button
              classes={classes.createButton}
              onClick={createWorkflow}
              disabled={!site}>
              {ifWindowWidthLessThan(TABLET_BREAKPOINT) ? (
                <Add className="sfcb-icon" />
              ) : (
                formatMessage(messages.createNewJob)
              )}
            </Button>
          )}
      </Grid>
      {ifWindowWidthLessThan(TABLET_BREAKPOINT) ? (
        <SidePopover
          open={isOpenMobileFilter}
          close={() => setMobileFilter(false)}
          position="bottom"
          className="mobile-filter"
          classes={{
            paper: 'mobile-filter-paper',
          }}>
          <div className="mobile-filter-header">
            <IconButton
              className="icon-btn close-btn"
              onClick={() => setMobileFilter(false)}>
              <Close />
            </IconButton>
            <Typography variant="h6">{formatMessage('filter_by')}</Typography>
            <Button
              size="small"
              className={cn('clear-btn', { disabled: !isShowBadge })}
              onClick={handleResetFilters}
              disabled={!isShowBadge}>
              {formatMessage('clear')}
            </Button>
          </div>
          <div className="mobile-filter-body">
            <div className="filter-group">
              <div className="form-group">
                <SiteFilter activeSite={site} setSite={setSitesFilter} />
              </div>
              <div className="form-group">
                <UnitFilter
                  savedUnitIds={savedUnitIds}
                  unitList={locusesAndGroups}
                  units={selectedUnits}
                  setUnits={setUnitsFilter}
                />
              </div>
            </div>
            <div className="filter-group">
              <div className="form-group">
                <JobsFilter
                  jobList={workflows}
                  workflows={selectedWorkflows}
                  setWorkflows={setWorkflows}
                  saveFilter={saveJobFilters}
                />
              </div>
              <div className="form-group">
                <CreatorsFilter
                  creatorList={creators}
                  selectedCreators={selectedCreators}
                  triggerRules={triggerRules}
                  selectedTriggerRules={selectedTriggerRules}
                  setCreatedBy={setCreatedBy}
                />
              </div>
              <div className="form-group">
                <AssigneesFilter
                  assigneesList={assignees}
                  assignees={selectedAssignees}
                  setAssignees={setAssignees}
                  saveFilter={saveJobFilters}
                />
              </div>
            </div>
            {featureFlags.JOBS_APP_SORTING && (
              <div className="filter-group">
                <SortFilter
                  currentSorting={currentSorting}
                  saveSorting={saveSorting}
                />
              </div>
            )}
            <div className="show-button-box">
              <Button
                fullWidth
                size="large"
                color="primary"
                variant="contained"
                className="show-button"
                onClick={() => setMobileFilter(false)}>
                {showButtonText}
              </Button>
            </div>
          </div>
        </SidePopover>
      ) : (
        <Collapse in={isOpenFilters} className="jobs-collapsed-filter">
          <Grid container spacing={2}>
            <Grid item sm={2}>
              <SiteFilter activeSite={site} setSite={setSitesFilter} />
            </Grid>
            <Grid item sm={2}>
              <UnitFilter
                savedUnitIds={savedUnitIds}
                unitList={locusesAndGroups}
                units={selectedUnits}
                setUnits={setUnitsFilter}
              />
            </Grid>
            <Grid item sm={2}>
              <JobsFilter
                jobList={workflows}
                workflows={selectedWorkflows}
                setWorkflows={setWorkflows}
                saveFilter={saveJobFilters}
              />
            </Grid>
            <Grid item sm={2}>
              <CreatorsFilter
                creatorList={creators}
                selectedCreators={selectedCreators}
                triggerRules={triggerRules}
                selectedTriggerRules={selectedTriggerRules}
                setCreatedBy={setCreatedBy}
              />
            </Grid>
            <Grid item sm={2}>
              <AssigneesFilter
                assigneesList={assignees}
                assignees={selectedAssignees}
                setAssignees={setAssignees}
                saveFilter={saveJobFilters}
              />
            </Grid>
          </Grid>
        </Collapse>
      )}
      {ifWindowWidthLessThan(TABLET_BREAKPOINT) && !featureFlags.APP_HEADER_V2 && (
        <div className="mobile-filter-block">
          <Badge variant="dot" invisible={!isShowBadge}>
            <IconButton
              className="icon-btn"
              onClick={() => setMobileFilter(true)}>
              <IconFilter />
            </IconButton>
          </Badge>
        </div>
      )}
    </div>
  )
}

export default connect(
  state => ({
    jobTask: state.getIn(['myJobs', 'jobTask']),
    isLoadJobList: state.getIn(['myJobs', 'isLoadJobList']),
    jobList: state.getIn(['myJobs', 'jobList']),
    jobFiltersData: state.getIn(['myJobs', 'jobsFiltersData']),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
    jobProfileFilters: state.getIn(['profile', 'myJobsFilters']),
    allowedToCreate: state.getIn(['profile', 'profile', 'grantedCreateJobs']),
    siteList: state.getIn(['logbookForm', 'siteList']),
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']),
    myJobsSorting: state.getIn(['profile', 'myJobsSorting']),
  }),
  {
    fetchJobList,
    fetchSiteList,
    fetchJobFiltersData,
    saveMyJobsFilters,
    setActiveSite,
    setJobFilters,
    resetTasks,
    saveJobsSorting,
  },
)(withRouter(FilterBar))
